import { z } from "zod";
import { Gender } from "@/openapi/generated";

const employeeRoleSchema = z.enum(["Teacher", "Staff", "Choreographer"]);

const dateParts = z.object({
  day: z.string(),
  month: z.string(),
  year: z.string(),
});

export const employeeCreateFormSchema = z
  .object({
    role: z.array(employeeRoleSchema),
    email: z.string().refine((val) => /\S+@\S+\.\S+/.test(val), {
      message: "Please enter a valid email address",
    }),
    firstName: z.string().min(1, { message: "First name is required" }),
    lastName: z.string().min(1, { message: "Last name is required" }),
    preferredName: z.string().optional().nullable(),
    cellPhone: z.string().optional().nullable(),
    homePhone: z.string().optional().nullable(),
    workLocationIds: z.array(z.number()),
    isActive: z.boolean().optional(),
    isFeatured: z.boolean().optional(),
    isInContactUsMailingList: z.boolean().optional(),
    payRate: z.string().optional().nullable(),
    payrollId: z.string().optional().nullable(),
    biography: z.string().optional().nullable(),
    address: z.string().optional().nullable(),
    city: z.string().optional().nullable(),
    state: z.string().optional().nullable(),
    zip: z.string().optional().nullable(),
    country: z.string().optional().nullable(),
    gender: z
      .string()
      .optional()
      .nullable()
      .refine(
        (val) =>
          val === undefined ||
          val === null ||
          val === "" ||
          val === Gender.Male ||
          val === Gender.Female ||
          val === Gender.Other,
        { message: "Invalid gender" },
      ),
    birthday: dateParts.optional().nullable(),
    startDate: z.union([z.string(), dateParts]).optional().nullable(),
    endDate: z.union([z.string(), dateParts]).optional().nullable(),
  })
  .superRefine((data, ctx) => {
    if (data.role.length === 0) {
      ctx.addIssue({
        code: "custom",
        path: ["role"],
        message: "Choose at least one role",
      });
    }

    const needsPay =
      data.role.includes("Teacher") || data.role.includes("Staff");
    const payText = data.payRate?.trim() ?? "";
    if (needsPay && payText === "") {
      ctx.addIssue({
        code: "custom",
        path: ["payRate"],
        message: "Pay rate is required",
      });
    } else if (payText !== "") {
      const amount = parseFloat(payText);
      if (Number.isNaN(amount) || amount < 0) {
        ctx.addIssue({
          code: "custom",
          path: ["payRate"],
          message: "Pay rate must be 0 or greater",
        });
      }
    }

    if (data.role.includes("Teacher") && data.workLocationIds.length === 0) {
      ctx.addIssue({
        code: "custom",
        path: ["workLocationIds"],
        message: "At least one location is required",
      });
    }
  });

export type EmployeeCreateFormSchemaType = z.infer<
  typeof employeeCreateFormSchema
>;
